import {
  db,
  type ActivityPriority,
  type ActivityRow,
  type ActivityCategory,
  type ActivitySourceType,
  type ActivityStatus,
  type EntityRef,
} from '../db';
import { newId } from '../../utils/ids';
import { AbstractBaseRepo } from './baseRepo';
import { updateSyncMetadata } from '../sync/syncMetadata';
import { trackEntityCreated, trackEntityDeleted } from '@/lib/metrics';

const PRIORITY_ORDER: Record<ActivityPriority, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const STATUS_ORDER: Record<ActivityStatus, number> = {
  in_progress: 0,
  pending: 1,
  completed: 2,
};

/** Sort activities by status (in_progress first, then pending, then completed) and due date (earliest first). */
function sortActivities(items: ActivityRow[]): ActivityRow[] {
  return items.sort((a, b) => {
    // Status: in_progress → pending → completed
    const statusDiff = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    if (statusDiff !== 0) return statusDiff;

    // Due date: earliest first
    if (a.dueDate || b.dueDate) {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      const dueDiff = new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
      if (dueDiff !== 0) return dueDiff;
    }

    // Priority: high → medium → low
    const priorityDiff =
      (a.priority ? PRIORITY_ORDER[a.priority] : 3) - (b.priority ? PRIORITY_ORDER[b.priority] : 3);
    if (priorityDiff !== 0) return priorityDiff;

    // Updated at: newest first
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });
}

function filterActive(items: ActivityRow[]): ActivityRow[] {
  return items.filter((item) => !item.syncDeletedAt);
}

export interface ActivityInput {
  companyId: string;
  siteId?: string;
  reportId?: string;
  reportTitle?: string;
  reportVisitDate?: string;
  reportSiteName?: string;
  title: string;
  type: 'system' | 'internal' | 'external';
  category?: ActivityCategory;
  priority?: ActivityPriority;
  dueDate?: string;
  sourceType?: ActivitySourceType;
  sourceId?: string;
  description?: string;
  personId?: string;
  triggerDate?: string;
  reminderDaysBefore?: number;
  references?: EntityRef[];
}

class ActivitiesRepoImpl extends AbstractBaseRepo<
  ActivityRow,
  ActivityRow,
  ActivityRow,
  ActivityInput
> {
  protected readonly table = db.activities;

  protected getRowId(row: ActivityRow): string {
    return row.id;
  }

  protected toListModel(row: ActivityRow): ActivityRow {
    return row;
  }

  protected toGetModel(row: ActivityRow): ActivityRow {
    return row;
  }

  protected toRow(input: ActivityInput, ctx: { now: string; existing?: ActivityRow }): ActivityRow {
    if (!input.companyId) {
      throw new Error("companyId obbligatorio per creare un'attività");
    }

    const id = newId();
    return {
      id,
      companyId: input.companyId,
      siteId: input.siteId,
      reportId: input.reportId,
      reportTitle: input.reportTitle?.trim() || undefined,
      reportVisitDate: input.reportVisitDate,
      reportSiteName: input.reportSiteName?.trim() || undefined,
      title: input.title.trim(),
      type: input.type,
      category: input.category ?? 'manual',
      status: 'pending',
      priority: input.priority ?? 'medium',
      dueDate: input.dueDate,
      sourceType: input.sourceType,
      sourceId: input.sourceId,
      description: input.description?.trim() || undefined,
      personId: input.personId,
      triggerDate: input.triggerDate,
      reminderDaysBefore: input.reminderDaysBefore,
      references: input.references,
      createdAt: ctx.now,
      updatedAt: ctx.now,
      // Sync metadata - will be overwritten by baseRepo.save()
      syncUpdatedAt: ctx.existing?.syncUpdatedAt ?? ctx.now,
      syncDeletedAt: ctx.existing?.syncDeletedAt,
      syncDeviceId: ctx.existing?.syncDeviceId,
      syncVersion: ctx.existing?.syncVersion ?? 0,
    };
  }

  async listByCompany(companyId: string): Promise<ActivityRow[]> {
    return db.activities
      .where('companyId')
      .equals(companyId)
      .toArray()
      .then((items) => sortActivities(filterActive(items)));
  }

  async listBySite(companyId: string, siteId: string): Promise<ActivityRow[]> {
    return db.activities
      .where('companyId')
      .equals(companyId)
      .filter((item) => item.siteId === siteId)
      .toArray()
      .then((items) => sortActivities(filterActive(items)));
  }

  async listAll(): Promise<ActivityRow[]> {
    const items = await this.table.toArray();
    return sortActivities(filterActive(items));
  }

  async listByReport(reportId: string): Promise<ActivityRow[]> {
    return db.activities
      .where('reportId')
      .equals(reportId)
      .toArray()
      .then((items) => sortActivities(filterActive(items)));
  }

  async create(input: ActivityInput): Promise<string> {
    const id = await this.save(input);
    trackEntityCreated('activity');
    return id;
  }

  override async softDelete(id: string): Promise<void> {
    await super.softDelete(id);
    trackEntityDeleted('activity');
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Workflow Methods
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Start work on an activity (pending → in_progress).
   * Sets startedAt timestamp.
   */
  async startActivity(id: string): Promise<void> {
    const existing = await this.table.get(id);
    if (!existing) return;
    if (existing.status !== 'pending') return;

    const now = this.now();
    const syncMeta = updateSyncMetadata(existing);
    await this.table.update(id, {
      status: 'in_progress',
      startedAt: now,
      updatedAt: now,
      ...syncMeta,
    });
  }

  /**
   * Complete an activity (pending|in_progress → completed).
   * Sets completedAt timestamp.
   */
  async completeActivity(id: string): Promise<void> {
    const existing = await this.table.get(id);
    if (!existing) return;
    if (existing.status === 'completed') return;

    const now = this.now();
    const syncMeta = updateSyncMetadata(existing);
    await this.table.update(id, {
      status: 'completed',
      completedAt: now,
      // Set startedAt if not already set (quick complete from pending)
      startedAt: existing.startedAt ?? now,
      updatedAt: now,
      ...syncMeta,
    });
  }

  /**
   * Reopen a completed activity (completed → pending).
   * Clears workflow timestamps.
   */
  async reopenActivity(id: string): Promise<void> {
    const existing = await this.table.get(id);
    if (!existing) return;
    if (existing.status !== 'completed') return;

    const now = this.now();
    const syncMeta = updateSyncMetadata(existing);
    await this.table.update(id, {
      status: 'pending',
      startedAt: undefined,
      completedAt: undefined,
      updatedAt: now,
      ...syncMeta,
    });
  }

  /**
   * @deprecated Use completeActivity instead
   */
  async markCompleted(id: string): Promise<void> {
    return this.completeActivity(id);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Source-Based Operations (for sync/deduplication)
  // ─────────────────────────────────────────────────────────────────────────────

  /**
   * Find an activity by its source for deduplication.
   * @param sourceType The type of source (e.g., "report", "personnel")
   * @param sourceId The unique source identifier
   */
  async findBySource(
    sourceType: ActivitySourceType,
    sourceId: string
  ): Promise<ActivityRow | undefined> {
    return this.table
      .where('sourceId')
      .equals(sourceId)
      .filter((item) => item.sourceType === sourceType && !item.syncDeletedAt)
      .first();
  }

  /**
   * Upsert an activity from an external source.
   * Creates if not exists, updates if exists and not completed.
   * @returns The activity ID
   */
  async upsertFromSource(input: ActivityInput): Promise<string> {
    if (!input.sourceType || !input.sourceId) {
      throw new Error('sourceType and sourceId required for upsertFromSource');
    }

    const existing = await this.findBySource(input.sourceType, input.sourceId);
    const now = this.now();

    if (existing) {
      // Don't update completed activities
      if (existing.status === 'completed') {
        return existing.id;
      }

      // Update existing activity
      const syncMeta = updateSyncMetadata(existing);
      await this.table.update(existing.id, {
        title: input.title.trim(),
        dueDate: input.dueDate,
        description: input.description?.trim() || undefined,
        reportId: input.reportId,
        reportTitle: input.reportTitle?.trim() || undefined,
        reportVisitDate: input.reportVisitDate,
        reportSiteName: input.reportSiteName?.trim() || undefined,
        triggerDate: input.triggerDate,
        reminderDaysBefore: input.reminderDaysBefore,
        references: input.references,
        updatedAt: now,
        ...syncMeta,
      });
      return existing.id;
    }

    // Create new activity
    return this.save(input);
  }

  /**
   * List activities by category/categories.
   */
  async listByCategory(categories: ActivityCategory[]): Promise<ActivityRow[]> {
    const items = await this.table
      .filter(
        (item) =>
          !item.syncDeletedAt && item.category !== undefined && categories.includes(item.category)
      )
      .toArray();
    return sortActivities(items);
  }

  /**
   * List pending/in_progress activities by category/categories.
   */
  async listOpenByCategory(categories: ActivityCategory[]): Promise<ActivityRow[]> {
    const items = await this.table
      .filter(
        (item) =>
          !item.syncDeletedAt &&
          (item.status === 'pending' || item.status === 'in_progress') &&
          item.category !== undefined &&
          categories.includes(item.category)
      )
      .toArray();
    return sortActivities(items);
  }

  /**
   * List pending activities (not started).
   */
  async listPending(): Promise<ActivityRow[]> {
    const items = await this.table
      .where('status')
      .equals('pending')
      .filter((item) => !item.syncDeletedAt)
      .toArray();
    return sortActivities(items);
  }

  /**
   * List in-progress activities.
   */
  async listInProgress(): Promise<ActivityRow[]> {
    const items = await this.table
      .where('status')
      .equals('in_progress')
      .filter((item) => !item.syncDeletedAt)
      .toArray();
    return sortActivities(items);
  }

  /**
   * List activities due within N days.
   */
  async listDueSoon(days: number): Promise<ActivityRow[]> {
    const now = new Date();
    const cutoff = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
    const cutoffIso = cutoff.toISOString().split('T')[0];

    const items = await this.table
      .filter(
        (item) =>
          !item.syncDeletedAt &&
          item.status !== 'completed' &&
          item.dueDate !== undefined &&
          item.dueDate <= cutoffIso!
      )
      .toArray();
    return sortActivities(items);
  }

  /**
   * List overdue activities.
   */
  async listOverdue(): Promise<ActivityRow[]> {
    const todayIso = new Date().toISOString().split('T')[0];

    const items = await this.table
      .filter(
        (item) =>
          !item.syncDeletedAt &&
          item.status !== 'completed' &&
          item.dueDate !== undefined &&
          item.dueDate < todayIso!
      )
      .toArray();
    return sortActivities(items);
  }

  /**
   * Close an activity by its source (mark as completed).
   */
  async closeBySource(sourceType: ActivitySourceType, sourceId: string): Promise<void> {
    const existing = await this.findBySource(sourceType, sourceId);
    if (existing && existing.status !== 'completed') {
      await this.completeActivity(existing.id);
    }
  }

  /**
   * Delete an activity by its source (soft delete).
   */
  async deleteBySource(sourceType: ActivitySourceType, sourceId: string): Promise<void> {
    const existing = await this.findBySource(sourceType, sourceId);
    if (existing) {
      await this.softDelete(existing.id);
    }
  }

  /**
   * Add an entity reference to an activity.
   */
  async addReference(id: string, ref: EntityRef): Promise<void> {
    const existing = await this.table.get(id);
    if (!existing) return;

    const now = this.now();
    const syncMeta = updateSyncMetadata(existing);
    const references = existing.references ?? [];

    // Avoid duplicates
    const exists = references.some((r) => r.type === ref.type && r.id === ref.id);
    if (exists) return;

    await this.table.update(id, {
      references: [...references, ref],
      updatedAt: now,
      ...syncMeta,
    });
  }
}

export const ActivitiesRepo = new ActivitiesRepoImpl();

// Backward compatibility aliases
/** @deprecated Use ActivitiesRepo instead */
export const ActionsRepo = ActivitiesRepo;

// Re-export types from db.ts
export type {
  ActivityRow,
  ActivityCategory,
  ActivitySourceType,
  ActivityStatus,
  ActivityPriority,
  EntityRef,
  // Backward compatibility aliases
  ActionRow,
  ActionCategory,
  ActionSourceType,
} from '../db';
